import React, { useState } from 'react';
import styles from './AdminInfoForm.module.scss';
import { Row } from 'antd';
import Logo from 'assets/logo/logo_full.png';
import InputField from 'shared/components/InputField';
import { Formik, Form as FormikForm, FormikHelpers, FormikProps } from 'formik';
import COButton from 'shared/components/COButton';
import COCheckbox from 'shared/components/COCheckbox';
import { object, string, ref } from 'yup';
import { Fields } from './AdminInfoFormConstants';
import { AdminInfoFormProps } from 'enums/AdminInfoFormEnums';
import AuthenticationService from 'services/AuthenticationService/authentication.service';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import TermsAndConditionModal from './TermsAndConditionModal';
import PasswordValidation from 'shared/validation/PasswordValidation';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';

interface AdminInfoFormValues {
  name: string;
  position: string;
  email: string;
  phone?: string;
  createPassword: string;
  confirmPassword: string;
  terms: boolean;
}

interface LocalStorageFormValues {
  name: string;
  position: string;
  email: string;
  phone?: string;
  extension: string;
  password: string;
}

const AdminInfoForm: React.FC = () => {
  const navigate = useNavigate();
  const { sendVerificationEmail, emailExists } = AuthenticationService();
  const [showTermsAndCondition, setShowTermsAndCondition] =
    useState<boolean>(false);

  const {
    REQUIRED,
    EMAIL_EXISTS,
    UNIQUE_EMAIL,
    ENTER_VALID_EMAIL,
    PASSWORD_CRITERIA,
    PASSWORD_CRITERIA_2,
    CREATE_PASSWORD,
    CONFIRM_PASSWORD,
    LOCAL_STORAGE_FORM,
  } = AdminInfoFormProps;

  // Validation Schema
  const phoneRegExp = /^\d{10,15}$/;
  const validationSchema = object({
    name: string().required(REQUIRED).nullable(),
    position: string().required(REQUIRED).nullable(),
    email: string()
      .required(REQUIRED)
      .test(UNIQUE_EMAIL, EMAIL_EXISTS, async value => {
        let isEmailUnique;
        if (value) {
          isEmailUnique = await emailExists(value);
        }
        return !isEmailUnique;
      })
      .email(ENTER_VALID_EMAIL)
      .nullable(),
    phone: string()
      .matches(
        phoneRegExp,
        'Phone number must be between 10 and 15 digits and can only contain numbers'
      )
      .optional()
      .nullable(),
    createPassword: string()
      .required(REQUIRED)
      .min(8, PASSWORD_CRITERIA)
      .matches(PasswordValidation, PASSWORD_CRITERIA_2)
      .nullable(),
    confirmPassword: string()
      .oneOf([ref(CREATE_PASSWORD), undefined], CONFIRM_PASSWORD)
      .required(REQUIRED)
      .nullable(),
  });

  const handleCheckboxChange = (
    e: CheckboxChangeEvent,
    formik: FormikProps<AdminInfoFormValues>
  ) => {
    formik.setFieldValue(AdminInfoFormProps.CHECKBOX_NAME, e.target.checked);
  };

  const handleApiSubmission = async (
    values: AdminInfoFormValues,
    formikHelpers: FormikHelpers<AdminInfoFormValues>
  ) => {
    try {
      const registeredUser: LocalStorageFormValues = {
        name: values.name,
        position: values.position,
        email: values.email,
        phone: values.phone || '',
        extension: AdminInfoFormProps.EXTENSION,
        password: values.createPassword,
      };
      localStorage.setItem(LOCAL_STORAGE_FORM, JSON.stringify(registeredUser));
      await sendVerificationEmail({ email: values.email, name: values.name });
    } catch (error) {
      console.error('Something went wrong!');
    } finally {
      formikHelpers.setSubmitting(false);
    }
    navigate(AppRoutes.VERIFY_EMAIL, { state: values.email });
  };

  const handleLoginRedirection = () => {
    navigate(AppRoutes.LOGIN_VIEW);
  };

  const onCheckBoxLabelClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setShowTermsAndCondition(true);
  };

  const onAcceptingTermsAndCondition = (
    setFieldValue: FormikProps<AdminInfoFormValues>['setFieldValue']
  ) => {
    setFieldValue(AdminInfoFormProps.CHECKBOX_NAME, true);
    setShowTermsAndCondition(false);
  };

  const onRejectingTermsAndCondition = (
    setFieldValue: FormikProps<AdminInfoFormValues>['setFieldValue']
  ) => {
    setFieldValue(AdminInfoFormProps.CHECKBOX_NAME, false);
    setShowTermsAndCondition(false);
  };

  // Fetching form values from localStorage (stored to persist between pages)
  const signupFormDetails = localStorage.getItem(LOCAL_STORAGE_FORM);
  const parsedForm: Partial<AdminInfoFormValues> = signupFormDetails
    ? JSON.parse(signupFormDetails)
    : {};

  const initialValues: AdminInfoFormValues = {
    name: '',
    position: '',
    email: '',
    phone: '',
    createPassword: '',
    confirmPassword: '',
    terms: false,
    ...parsedForm,
  };

  return (
    <div className={styles.formContainer}>
      <Row className={styles.logoContainer}>
        <img
          src={Logo}
          alt={AdminInfoFormProps.IMAGE_ALT}
          className={styles.logo}
        />
      </Row>
      <Row className={styles.headingContainer}>
        <div>
          <h1>{AdminInfoFormProps.HEADING}</h1>
        </div>
        <div
          onClick={handleLoginRedirection}
          className={styles.subheadingContainer}>
          <h2>
            {AdminInfoFormProps.SUBHEADING}{' '}
            <span className={styles.spanText}>
              {AdminInfoFormProps.SPAN_SUBHEADING}
            </span>
          </h2>
        </div>
      </Row>
      <div className={styles.fieldsContainer}>
        <Formik
          initialValues={initialValues}
          validateOnBlur={true}
          validateOnChange={false}
          validationSchema={validationSchema}
          onSubmit={(values, formikHelpers) =>
            handleApiSubmission(values, formikHelpers)
          }>
          {formik => (
            <FormikForm>
              {Fields.map((field, index) => {
                return (
                  <div key={index} className={styles.fieldContainer}>
                    <InputField
                      addonbefore={field.addonbefore}
                      name={field.name}
                      placeholderstring={field.placeholder}
                      label={field.label}
                      password={field.password}
                    />
                  </div>
                );
              })}
              <div className={styles.checkboxContainer}>
                <COCheckbox
                  name={AdminInfoFormProps.CHECKBOX_NAME}
                  label={
                    <div
                      className={styles.termsText}
                      onClick={onCheckBoxLabelClick}>
                      {AdminInfoFormProps.HELPER_TEXT}{' '}
                      <span>{AdminInfoFormProps.HELPER_SPAN}</span>
                    </div>
                  }
                  onChange={e => handleCheckboxChange(e, formik)}
                />
              </div>
              <div className={styles.buttonContainer}>
                <COButton
                  submit
                  loading={formik.isSubmitting}
                  disabled={
                    !formik.isValid ||
                    Object.values(formik.values).every(value => !value) ||
                    formik.getFieldProps(AdminInfoFormProps.CHECKBOX_NAME)
                      .value !== true
                  }>
                  {AdminInfoFormProps.BUTTON_LABEL}
                </COButton>
              </div>
              <TermsAndConditionModal
                isModalOpen={showTermsAndCondition}
                hideModal={() => setShowTermsAndCondition(false)}
                onAccept={() =>
                  onAcceptingTermsAndCondition(formik.setFieldValue)
                }
                onReject={() => {
                  onRejectingTermsAndCondition(formik.setFieldValue);
                }}
              />
            </FormikForm>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AdminInfoForm;
